import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const DEFAULT_SETTINGS = { chef_show_prices: false, chef_show_suppliers: false };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch { /* not signed in */ }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await refreshSettings();
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshSettings]);

  const login = async (email, password, twofa_code) => {
    const { data } = await api.post("/auth/login", { email, password, twofa_code });
    if (data.twofa_required) {
      return { twofa_required: true, email: data.email };
    }
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    await refreshSettings();
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    await refreshSettings();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("stockly_cart_v1");
    setUser(false);
    setSettings(DEFAULT_SETTINGS);
  };

  const updateSettings = async (patch) => {
    const { data } = await api.patch("/settings", patch);
    setSettings(data);
    return data;
  };

  const showPrices = ["admin", "manager", "organization_admin", "super_admin", "platform_owner"].includes(user?.role) || !!settings.chef_show_prices;
  const showSuppliers = ["admin", "manager", "organization_admin", "super_admin", "platform_owner"].includes(user?.role) || !!settings.chef_show_suppliers;
  const isPlatform = ["super_admin", "support"].includes(user?.role);
  const isOrgAdmin = ["admin", "organization_admin", "manager"].includes(user?.role);
  const isManager = user?.role === "manager";
  const isChef = ["chef", "bediening"].includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, settings, updateSettings, showPrices, showSuppliers, refreshUser, isPlatform, isOrgAdmin, isManager, isChef }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
